import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import {
  Box,
  CssBaseline,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Typography,
  LinearProgress,
  Tooltip,
  Popover,
  Button,
  FormControlLabel,
  TextField,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider, useTheme } from '@mui/material/styles';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ClearIcon from '@mui/icons-material/Clear';
import { TRACKING_API_URL } from '../config';
/* ---------- THEME ---------- */
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: {
    h5: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

/* ---------- HELPER COMPONENTS ---------- */
const NumericCell = ({ value }) => {
  if (value === '-' || value === '' || value == null) return '-';
  const formatted = value >= 0 ? `+${value}` : `${value}`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted.padStart(5, ' ')}
      </Typography>
    </Box>
  );
};

const PercentageCell = ({ value }) => {
  if (value === '' || value == null) return '-';
  const val = value * 100;
  let red, green, blue;

  if (val <= -5) {
    red = 255;
    green = 0;
    blue = 0;
  } else if (val >= 5) {
    red = 0;
    green = 255;
    blue = 0;
  } else if (val < 0) {
    const ratio = (val + 10) / 10;
    red = 255;
    green = blue = Math.round(255 * ratio);
  } else {
    const ratio = val / 10;
    green = 255;
    red = blue = Math.round(255 * (1 - ratio));
  }

  const formatted = val.toFixed(1).padStart(val < 0 ? 4 : 3, ' ') + '%';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ color: `rgb(${red},${green},${blue})`, fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted.padStart(val < 0 ? 6 : 5, ' ')}
      </Typography>
    </Box>
  );
};

const LineCell = ({ value }) => {
  if (value === '-' || value === '' || value == null) return '-';
  const match = value.match(/^([ou])?(\d+(\.\d)?)$/);
  if (!match) return value;
  const [, prefix = '', number] = match;
  const formatted = number.padStart(3, ' ');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {prefix + formatted}
      </Typography>
    </Box>
  );
};

// Derive no-vig implied probability from American odds
const priceToBreakEvenWin = (price) => {
  if (price == null) return null;
  if (price < 0) return Math.abs(price) / (Math.abs(price) + 100);
  if (price > 0) return 100 / (price + 100);
  return null;
};

/* ---------- COMPONENT ---------- */
export default function BetTracker({ username, isAuthenticated }) {
  const betsData = useStoreData('odds');
  const { bookLogos } = useLogoMaps();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /* ---------- STATE ---------- */
  const [pageSize, setPageSize] = useState(50);
  const [sortModel, setSortModel] = useState([{ field: 'trackedAt', sort: 'desc' }]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [betTypeFilter, setBetTypeFilter] = useState([]);
  const [bookFilter, setBookFilter] = useState([]);
  const [sourceTypeFilter, setSourceTypeFilter] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [hidePastBets, setHidePastBets] = useState(() => {
    const saved = localStorage.getItem('hidePastBets');
    return saved ? JSON.parse(saved) : false;
  });
  const filtersInitialized = useRef(false);

  // ── Tracked Bets (self-managed via REST API) ──
  const [trackedBets, setTrackedBets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${TRACKING_API_URL}?username=${encodeURIComponent(username)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          // Normalize DB column names → display field names
          const bets = (data.tracked_bets || []).map(b => {
            const side = b.side === 'Over' ? 'o' : b.side === 'Under' ? 'u' : '';
            return {
              id: b.id,
              oddsId: b.odds_id,
              fixtureId: b.fixture_id,
              leagueName: b.league,
              eventShortName: b.event_name,
              eventStart: b.event_start,
              marketSourceName: b.sportsbook,
              sportsbook_logo: b.sportsbook_logo,
              playerName: b.player_name,
              marketId: b.market_id,
              betTypeName: b.market_display,
              sourceType: b.source_type || '',
              side: b.side,
              points: b.points,
              price: b.price,
              roi: b.roi,
              homeTeam: b.home_team,
              awayTeam: b.away_team,
              homeLogo: b.home_logo,
              awayLogo: b.away_logo,
              trackedAt: b.tracked_at,
              result: b.result,
              actual: b.actual,
              sourceKey: b.source_key,
              sidepoints: `${side}${b.points || ''}`.trim(),
              // Server-computed CLV (handles cross-line via projection lookup)
              clv: b.clv,
              breakEvenWin: b.breakEvenWin,
              liveEV: b.liveEV,
              // Closing snapshot data (populated for past games via OUTER APPLY)
              closingPrice: b.closing_price,
              closingPoints: b.closing_points,
              closingTrueWin: b.closing_trueWin,
              closingBreakEvenWin: b.closing_breakEvenWin,
            };
          });
          setTrackedBets(bets);
        }
      } catch (err) {
        console.error('[BET-TRACKER] Failed to fetch tracked bets:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username, isAuthenticated]);

  // ── Untrack via REST API ──
  const handleUntrack = async (oddsId) => {
    if (!username) return;
    try {
      const resp = await fetch(
        `${TRACKING_API_URL}/${encodeURIComponent(oddsId)}?username=${encodeURIComponent(username)}`,
        { method: 'DELETE' }
      );
      if (!resp.ok && resp.status !== 404) throw new Error(`HTTP ${resp.status}`);
      setTrackedBets(prev => prev.filter(b => b.oddsId !== oddsId));
    } catch (err) {
      console.error('[BET-TRACKER] Untrack failed:', err.message);
    }
  };

  /* ---------- PERSIST SETTINGS ---------- */
  useEffect(() => {
    localStorage.setItem('hidePastBets', JSON.stringify(hidePastBets));
  }, [hidePastBets]);

  /* ---------- SETTINGS POPOVER HANDLERS ---------- */
  const handleSettingsOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleSettingsSave = () => {
    setSettingsAnchorEl(null);
  };

  /* ---------- DATE RANGE ---------- */
  const DATE_PRESETS = [
    { value: 'all', label: 'All Time' },
    { value: '1', label: 'Today' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
  ];

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setDateRange(val);
    if (val === 'all') {
      setCustomDateFrom('');
      setCustomDateTo('');
    } else {
      const days = parseInt(val, 10);
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - days);
      setCustomDateFrom(from.toISOString().slice(0, 10));
      setCustomDateTo(to.toISOString().slice(0, 10));
    }
  };

  const handleCustomDateFrom = (e) => {
    setCustomDateFrom(e.target.value);
    setDateRange('custom');
  };
  const handleCustomDateTo = (e) => {
    setCustomDateTo(e.target.value);
    setDateRange('custom');
  };

  const { dateFrom, dateTo } = useMemo(() => {
    if (dateRange === 'all') return { dateFrom: '', dateTo: '' };
    if (dateRange === 'custom') return { dateFrom: customDateFrom, dateTo: customDateTo };
    const days = parseInt(dateRange, 10);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    return { dateFrom: from.toISOString().slice(0, 10), dateTo: to.toISOString().slice(0, 10) };
  }, [dateRange, customDateFrom, customDateTo]);

  const dateChipLabel = useMemo(() => {
    const preset = DATE_PRESETS.find(p => p.value === dateRange);
    if (preset) return preset.label;
    if (customDateFrom && customDateTo) return `${customDateFrom} – ${customDateTo}`;
    if (customDateFrom) return `From ${customDateFrom}`;
    if (customDateTo) return `To ${customDateTo}`;
    return 'All Time';
  }, [dateRange, customDateFrom, customDateTo]);

  /* ---------- BUILD ODDS LOOKUP FOR LIVE DATA ---------- */
  // Primary lookup: fixture + book + player + market_id + side (unique per game + market)
  // Fallback: odds_id (for edge cases)
  const { liveKeyMap, oddsIdMap } = useMemo(() => {
    const keyMap = new Map();
    const idMap = new Map();
    for (const bet of betsData) {
      // Compound key: fixture_id ensures game-level markets don't collide
      const key = `${bet.fixture_id}|${bet.bookName}|${bet.playerName || ''}|${bet.market_id}|${(bet.side || '').toLowerCase()}`;
      keyMap.set(key, bet);
      // Also index by odds_id as fallback
      idMap.set(bet.id, bet);
    }
    return { liveKeyMap: keyMap, oddsIdMap: idMap };
  }, [betsData]);

  /* ---------- DYNAMIC FILTER OPTIONS FROM TRACKED BETS ---------- */
  const dynamicLeagues = useMemo(
    () => [...new Set(trackedBets.map((b) => b.leagueName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicBetTypes = useMemo(
    () => [...new Set(trackedBets.map((b) => b.betTypeName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicBooks = useMemo(
    () => [...new Set(trackedBets.map((b) => b.marketSourceName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicSourceTypes = useMemo(
    () => [...new Set(trackedBets.map((b) => b.sourceType).filter(Boolean))],
    [trackedBets]
  );

  /* ---------- INITIALIZE FILTERS WITH ALL OPTIONS (ONCE) ---------- */
  useEffect(() => {
    if (!filtersInitialized.current && dynamicLeagues.length > 0) {
      setLeagueFilter(dynamicLeagues);
      setBetTypeFilter(dynamicBetTypes);
      setBookFilter(dynamicBooks);
      setSourceTypeFilter(dynamicSourceTypes);
      filtersInitialized.current = true;
    }
  }, [dynamicLeagues, dynamicBetTypes, dynamicBooks, dynamicSourceTypes]);

  /* ---------- FILTER HELPERS ---------- */
  const handleFilterChange = (e, options, cur, setter) => {
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(cur.length === options.length ? [] : options);
    } else {
      setter(val);
    }
  };
  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple (${sel.length})` : sel.join(', ');
  };

  /* ---------- APPLY FILTERS AND DETERMINE LIVE STATUS ---------- */
  const { filteredBets, avgEV, avgLiveEV, avgOddsDelta, posCLV, negCLV, posCLVPct, wins, losses, pushes, winPct, avgBEW } = useMemo(() => {
    const allTrackedBets = trackedBets.map(trackedBet => {
      // Lookup live data: compound key first (survives line moves), odds_id fallback
      // For game-level markets (totals, spreads), playerName on tracked bet may contain
      // event name — live bets have null. Use empty string for non-player markets.
      const isPlayerMarket = (trackedBet.marketId || '').startsWith('player_');
      const keyPlayer = isPlayerMarket ? (trackedBet.playerName || '') : '';
      const liveKey = `${trackedBet.fixtureId}|${trackedBet.marketSourceName}|${keyPlayer}|${trackedBet.marketId}|${(trackedBet.side || '').toLowerCase()}`;
      const liveBet = liveKeyMap.get(liveKey) || oddsIdMap.get(trackedBet.oddsId) || {};
      const isPastGame = trackedBet.eventStart && new Date(trackedBet.eventStart) <= new Date();

      // Diagnostic: log when no live match found for upcoming bets
      if (!isPastGame && !liveKeyMap.has(liveKey) && !oddsIdMap.has(trackedBet.oddsId)) {
        console.log('[BetTracker Match] NO MATCH for:', {
          liveKey,
          fixtureId: trackedBet.fixtureId,
          marketSourceName: trackedBet.marketSourceName,
          playerName: trackedBet.playerName,
          marketId: trackedBet.marketId,
          side: trackedBet.side,
          oddsId: trackedBet.oddsId,
          sampleLiveKeys: [...liveKeyMap.keys()].slice(0, 5),
        });
      }

      let currentLine, currentOdds, liveEV, lineValue;

      if (isPastGame) {
        // Use closing snapshot data if available
        const closingSide = trackedBet.side === 'Over' ? 'o' : trackedBet.side === 'Under' ? 'u' : '';
        currentLine = trackedBet.closingPoints != null ? `${closingSide}${trackedBet.closingPoints}` : '-';
        currentOdds = trackedBet.closingPrice != null ? trackedBet.closingPrice : '-';

        const trackedBEW = trackedBet.breakEvenWin || priceToBreakEvenWin(trackedBet.price);
        const bothNull = trackedBet.points == null && trackedBet.closingPoints == null;
        const sameLineCheck = bothNull || (trackedBet.closingPoints != null && Math.abs(Number(trackedBet.points) - Number(trackedBet.closingPoints)) < 0.01);

        // Closing EV: closing trueWin vs what you paid (same line only)
        if (sameLineCheck && trackedBet.closingTrueWin != null && trackedBEW != null) {
          liveEV = (trackedBet.closingTrueWin / trackedBEW) - 1;
        } else {
          liveEV = null;
        }

        // CLV: use server-calculated CLV if available (handles cross-line),
        // fall back to frontend same-line calculation
        if (trackedBet.clv != null) {
          lineValue = trackedBet.clv;
        } else if (sameLineCheck && trackedBet.closingBreakEvenWin != null && trackedBEW != null) {
          lineValue = (trackedBet.closingBreakEvenWin / trackedBEW) - 1;
        } else {
          lineValue = null;
        }
      } else {
        // Show current line from this book (even if line moved)
        const side = liveBet.side === 'Over' ? 'o' : liveBet.side === 'Under' ? 'u' : '';
        currentLine = liveBet.points != null ? `${side}${liveBet.points}` : '-';
        currentOdds = liveBet.price != null ? liveBet.price : '-';

        const trackedBEW = trackedBet.breakEvenWin || priceToBreakEvenWin(trackedBet.price);
        const bothNull = trackedBet.points == null && liveBet.points == null;
        const sameLineCheck = bothNull || (liveBet.points != null && Math.abs(Number(trackedBet.points) - Number(liveBet.points)) < 0.01);

        // Live EV: use server-calculated liveEV if available (handles cross-line),
        // fall back to frontend same-line calculation
        if (trackedBet.liveEV != null) {
          liveEV = trackedBet.liveEV;
        } else if (liveBet.trueWin != null && trackedBEW != null && sameLineCheck) {
          liveEV = (liveBet.trueWin / trackedBEW) - 1;
        } else if (sameLineCheck && trackedBEW != null && liveBet.price != null) {
          const liveBEW = liveBet.breakEvenWin != null ? liveBet.breakEvenWin : priceToBreakEvenWin(liveBet.price);
          liveEV = liveBEW != null ? (liveBEW / trackedBEW) - 1 : null;
        } else {
          liveEV = null;
        }

        // CLV: use server-calculated CLV if available (handles cross-line),
        // fall back to frontend same-line calculation
        if (trackedBet.clv != null) {
          lineValue = trackedBet.clv;
        } else if (sameLineCheck && trackedBEW != null) {
          const liveBEW = liveBet.breakEvenWin != null ? liveBet.breakEvenWin : priceToBreakEvenWin(liveBet.price);
          lineValue = liveBEW != null ? (liveBEW / trackedBEW) - 1 : null;
        } else {
          lineValue = null;
        }
      }

      return {
        id: trackedBet.id,
        oddsId: trackedBet.oddsId,
        roi: trackedBet.roi,
        sidepoints: trackedBet.sidepoints,
        price: trackedBet.price,
        currentLine,
        currentOdds,
        liveEV,
        lineValue,
        leagueName: trackedBet.leagueName,
        eventShortName: trackedBet.eventShortName,
        eventStart: trackedBet.eventStart,
        awayLogo: trackedBet.awayLogo,
        homeLogo: trackedBet.homeLogo,
        playerName: trackedBet.playerName,
        betTypeName: trackedBet.betTypeName,
        marketSourceName: trackedBet.marketSourceName,
        sportsbook_logo: bookLogos[trackedBet.marketSourceName] || trackedBet.sportsbook_logo || '',
        sourceType: trackedBet.sourceType,
        isPastGame,
        trackedAt: trackedBet.trackedAt,
        result: trackedBet.result,
        actual: trackedBet.actual,
      };
    });

    const leagueSet = new Set(leagueFilter);
    const betTypeSet = new Set(betTypeFilter);
    const bookSet = new Set(bookFilter);
    const sourceTypeSet = new Set(sourceTypeFilter);

    const filtered = allTrackedBets.filter(bet => {
      const leagueMatch = leagueFilter.length === 0 || (bet.leagueName && leagueSet.has(bet.leagueName));
      const betTypeMatch = betTypeFilter.length === 0 || (bet.betTypeName && betTypeSet.has(bet.betTypeName));
      const bookMatch = bookFilter.length === 0 || (bet.marketSourceName && bookSet.has(bet.marketSourceName));
      const sourceMatch = sourceTypeFilter.length === 0 || !bet.sourceType || sourceTypeSet.has(bet.sourceType);
      const pastBetMatch = !hidePastBets || (bet.eventStart && new Date(bet.eventStart) > new Date());
      const dateFromMatch = !dateFrom || (bet.trackedAt && new Date(bet.trackedAt) >= new Date(dateFrom + 'T00:00:00'));
      const dateToMatch = !dateTo || (bet.trackedAt && new Date(bet.trackedAt) <= new Date(dateTo + 'T23:59:59'));
      return leagueMatch && betTypeMatch && bookMatch && sourceMatch && pastBetMatch && dateFromMatch && dateToMatch;
    });

    let evSum = 0, liveEVSum = 0, oddsDeltaSum = 0;
    let evCount = 0, liveEVCount = 0, oddsDeltaCount = 0;
    let wins = 0, losses = 0, pushes = 0;
    let bewSum = 0, bewCount = 0;
    let posCLV = 0, negCLV = 0;

    filtered.forEach(bet => {
      if (bet.roi != null) {
        evSum += bet.roi * 100;
        evCount++;
      }
      if (bet.liveEV != null) {
        liveEVSum += bet.liveEV * 100;
        liveEVCount++;
      }
      if (bet.lineValue != null) {
        oddsDeltaSum += bet.lineValue * 100;
        oddsDeltaCount++;
        if (bet.lineValue > 0) posCLV++;
        else if (bet.lineValue < 0) negCLV++;
      }
      if (bet.result === 'won') wins++;
      else if (bet.result === 'lost') losses++;
      else if (bet.result === 'push') pushes++;
      if (bet.price != null) {
        const bew = bet.price < 0 ? Math.abs(bet.price) / (Math.abs(bet.price) + 100) : bet.price > 0 ? 100 / (bet.price + 100) : null;
        if (bew != null) {
          bewSum += bew;
          bewCount++;
        }
      }
    });

    const finalFilteredBets = [...filtered].sort((a, b) => {
      const sortDirection = sortModel[0]?.sort === 'desc' ? -1 : 1;
      const field = sortModel[0]?.field || 'trackedAt';
      if (a[field] < b[field]) return -sortDirection;
      if (a[field] > b[field]) return sortDirection;
      return 0;
    });

    return {
      filteredBets: finalFilteredBets,
      avgEV: evCount > 0 ? (evSum / evCount).toFixed(1) : '-',
      avgLiveEV: liveEVCount > 0 ? (liveEVSum / liveEVCount).toFixed(1) : '-',
      avgOddsDelta: oddsDeltaCount > 0 ? (oddsDeltaSum / oddsDeltaCount).toFixed(1) : '-',
      posCLV,
      negCLV,
      posCLVPct: (posCLV + negCLV) > 0 ? (100 * posCLV / (posCLV + negCLV)).toFixed(1) : '-',
      wins,
      losses,
      pushes,
      winPct: (wins + losses) > 0 ? (100 * wins / (wins + losses)).toFixed(1) : '-',
      avgBEW: bewCount > 0 ? (100 * bewSum / bewCount).toFixed(1) : '-',
    };
  }, [trackedBets, liveKeyMap, oddsIdMap, leagueFilter, betTypeFilter, bookFilter, sourceTypeFilter, sortModel, hidePastBets, bookLogos, dateFrom, dateTo]);

  /* ---------- EXPORT TO CSV FUNCTION ---------- */
  const exportToCSV = () => {
    if (!filteredBets.length) return;

    const headers = [
      'Tracked At',
      'League',
      'Event',
      'Event Start',
      'Player',
      'Bet Type',
      'Book',
      'Source',
      'EV',
      'Line',
      'Live Line',
      'Odds',
      'Live Odds',
      'CLV',
      'Result',
      'Actual',
    ];

    const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', {
      timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).replace(',', '') : '';

    const rows = filteredBets.map(bet => [
      fmtDate(bet.trackedAt),
      bet.leagueName || '',
      bet.eventShortName || '',
      fmtDate(bet.eventStart),
      bet.playerName || '',
      bet.betTypeName || '',
      bet.marketSourceName || '',
      bet.sourceType || '',
      bet.roi != null ? `${(bet.roi * 100).toFixed(1)}%` : '-',
      bet.sidepoints || '-',
      bet.currentLine || '-',
      bet.price != null ? (bet.price >= 0 ? `+${bet.price}` : `${bet.price}`) : '-',
      bet.currentOdds != null && bet.currentOdds !== '-' ? (bet.currentOdds >= 0 ? `+${bet.currentOdds}` : `${bet.currentOdds}`) : '-',
      bet.lineValue != null ? `${(bet.lineValue * 100).toFixed(1)}%` : '-',
      bet.result || '-',
      bet.actual != null ? `${bet.actual}` : '-',
    ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bettracker_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* ---------- COLUMNS ---------- */
  const columns = useMemo(() => {
    const cols = [
      ...(!isMobile ? [{
        field: 'trackedAt',
        headerName: 'Tracked At',
        flex: 1,
        minWidth: 140,
        valueFormatter: v => {
          if (!v) return '';
          const d = new Date(v);
          const opts = { year: '2-digit', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
          const [mdy, t] = d.toLocaleString(undefined, opts).split(', ');
          return `${mdy} ${t.toLowerCase()}`;
        }
      }] : []),
      {
        field: 'eventShortName',
        headerName: 'Event',
        flex: 2,
        minWidth: isMobile ? 120 : 175,
        renderCell: (params) => {
          if (!params.value) return <Box sx={{ paddingLeft: '8px' }}>-</Box>;
          const eventStart = params.row.eventStart
            ? new Date(params.row.eventStart).toLocaleString('en-US', {
                year: '2-digit', month: 'numeric', day: 'numeric',
                hour: 'numeric', minute: '2-digit', hour12: true,
              }).replace(',', '')
            : '';
          const [awayTeam, homeTeam] = params.value.split('@');
          const leagueName = params.row.leagueName || '';
          return (
            <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
                {leagueName && (
                  <>
                    <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', color: '#ffffff', mr: 0 }}>{leagueName}</Typography>
                    <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', color: '#ffffff', mx: 0.5 }}>|</Typography>
                  </>
                )}
                {params.row.awayLogo && (
                  <img src={params.row.awayLogo} alt="" style={{ width: 'auto', height: isMobile ? '12px' : '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
                )}
                <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{awayTeam}</Typography>
                <Typography sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
                {params.row.homeLogo && (
                  <img src={params.row.homeLogo} alt="" style={{ width: 'auto', height: isMobile ? '12px' : '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
                )}
                <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{homeTeam}</Typography>
              </Box>
              <Typography sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'playerName',
        headerName: 'Bet Info',
        flex: 2,
        minWidth: isMobile ? 110 : 165,
        renderCell: (params) => {
          const playerName = params.row.playerName || '';
          const betTypeName = params.row.betTypeName || '';
          return (
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
              <Tooltip title={playerName} placement="top" arrow>
                <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', paddingLeft: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                  {playerName}
                </Typography>
              </Tooltip>
              <Tooltip title={betTypeName} placement="top" arrow>
                <Typography sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', paddingLeft: '8px', color: '#BBBBBB', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                  {betTypeName}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'marketSourceName',
        headerName: '',
        width: isMobile ? 36 : 52,
        minWidth: isMobile ? 36 : 52,
        maxWidth: isMobile ? 36 : 52,
        flex: 0,
        resizable: false,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (p) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 0 }}>
            <Tooltip title={p.row.marketSourceName || ''} placement="top" arrow>
              <img src={p.row.sportsbook_logo || ''} alt="Logo"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
            </Tooltip>
          </div>
        ),
      },
      ...(!isMobile ? [{
        field: 'sourceType',
        headerName: 'Source',
        flex: 1,
        minWidth: 75,
        sortable: false,
        renderCell: (p) => {
          const label = p.value === 'evtool' ? 'EvTool' : p.value === 'arbitrage' ? 'Arb' : p.value === 'middle' ? 'Middle' : p.value || '-';
          const color = p.value === 'arbitrage' ? '#ff9800' : p.value === 'middle' ? '#2196f3' : '#ffffff';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography sx={{ fontSize: '0.875rem', color, pl: '8px' }}>{label}</Typography>
            </Box>
          );
        },
      }] : []),
      {
        field: 'roi',
        headerName: 'EV',
        flex: 1,
        minWidth: isMobile ? 50 : 55,
        sortable: false,
        renderCell: (p) => <PercentageCell value={p.value} />,
      },
      {
        field: 'sidepoints',
        headerName: 'Line',
        minWidth: isMobile ? 50 : 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => <LineCell value={p.value} />,
      },
      ...(!isMobile ? [{
        field: 'currentLine',
        headerName: 'Live',
        minWidth: 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => <LineCell value={p.value} />,
      }] : []),
      {
        field: 'price',
        headerName: 'Odds',
        minWidth: isMobile ? 50 : 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => <NumericCell value={p.value} />,
      },
      ...(!isMobile ? [{
        field: 'currentOdds',
        headerName: 'Live',
        minWidth: 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => <NumericCell value={p.value} />,
      }] : []),
      {
        field: 'lineValue',
        headerName: 'CLV',
        minWidth: isMobile ? 50 : 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => <PercentageCell value={p.value} />,
      },
      {
        field: 'actual',
        headerName: 'Actual',
        minWidth: isMobile ? 45 : 60,
        flex: 1,
        sortable: false,
        renderCell: (p) => {
          if (p.value == null) return '-';
          const result = p.row.result;
          let color = '#ffffff';
          if (result === 'won') color = '#00ff00';
          if (result === 'lost') color = '#ff0000';
          if (result === 'push') color = '#ffff00';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
              <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', color }}>{p.value}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'untrack',
        headerName: '',
        sortable: false,
        width: isMobile ? 40 : 50,
        minWidth: isMobile ? 40 : 50,
        maxWidth: isMobile ? 40 : 50,
        flex: 0,
        resizable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <IconButton onClick={() => handleUntrack(params.row.oddsId)} size="small"
              sx={{ color: 'red', '&:hover': { color: '#ff5252' } }} disabled={!isAuthenticated}>
              <CheckBoxIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
            </IconButton>
          </Box>
        ),
      },
    ];
    return cols;
  }, [isMobile, isAuthenticated, bookLogos]);

  /* ---------- RENDER ---------- */
  const metricsData = [
    { label: 'EV', value: avgEV !== '-' ? `${avgEV}%` : '-' },
    { label: 'CLV', value: avgOddsDelta !== '-' ? `${avgOddsDelta}%` : '-' },
    { label: '+/-', value: `${posCLV}/${negCLV}` },
    { label: '+CLV%', value: posCLVPct !== '-' ? `${posCLVPct}%` : '-' },
    { label: 'W/L', value: `${wins}-${losses}${pushes > 0 ? `-${pushes}` : ''}` },
    { label: 'W%', value: winPct !== '-' ? `${winPct}%` : '-' },
    { label: 'BE%', value: avgBEW !== '-' ? `${avgBEW}%` : '-' },
  ];

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filters bar */}
          <Box className="filter-section" sx={{ px: isMobile ? 1 : 2, py: isMobile ? 0.5 : 1, flexShrink: 0 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 0.5 : 2} alignItems={isMobile ? 'stretch' : 'center'} justifyContent="space-between">
              {/* ------- filters row ------- */}
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: isMobile ? 0.5 : 2,
                alignItems: 'center',
                flex: 1,
              }}>
                {[
                  { label: 'League', items: dynamicLeagues, state: leagueFilter, setter: setLeagueFilter },
                  { label: 'Source', items: dynamicSourceTypes, state: sourceTypeFilter, setter: setSourceTypeFilter },
                  { label: 'Bet Type', items: dynamicBetTypes, state: betTypeFilter, setter: setBetTypeFilter },
                  { label: 'Book', items: dynamicBooks, state: bookFilter, setter: setBookFilter },
                ].map(({ label, items, state, setter }) => (
                  <FormControl key={label} variant="outlined" size="small" sx={{ minWidth: isMobile ? 0 : 120, flex: isMobile ? '1 1 calc(50% - 4px)' : 'none' }}>
                    <InputLabel id={`${label}-filter-label`} shrink sx={{ fontSize: '0.875rem' }}>
                      {label}
                    </InputLabel>
                    <Select
                      multiple
                      labelId={`${label}-filter-label`}
                      value={state}
                      label={label}
                      notched
                      onChange={(e) => handleFilterChange(e, items, state, setter)}
                      renderValue={(sel) => renderFilterValue(sel, items, label)}
                      sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={state.length === items.length} />
                        <ListItemText primary="Select All" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                      </MenuItem>
                      {items.map((val) => (
                        <MenuItem key={val} value={val}>
                          <Checkbox checked={state.includes(val)} />
                          <ListItemText primary={val} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}

                {/* Date range chip */}
                <Chip
                  icon={<DateRangeIcon sx={{ fontSize: '1rem', color: dateRange !== 'all' ? '#ff1744' : '#aaa' }} />}
                  label={isMobile && dateChipLabel.length > 12 ? dateChipLabel.slice(0, 12) + '…' : dateChipLabel}
                  onClick={(e) => setDateAnchorEl(e.currentTarget)}
                  onDelete={dateRange !== 'all' ? () => { setDateRange('all'); setCustomDateFrom(''); setCustomDateTo(''); } : undefined}
                  deleteIcon={<ClearIcon sx={{ fontSize: '0.9rem' }} />}
                  variant="outlined"
                  sx={{
                    color: '#ffffff',
                    borderColor: dateRange !== 'all' ? '#ff1744' : '#666',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    height: 32,
                    '& .MuiChip-deleteIcon': { color: '#aaa', '&:hover': { color: '#ff1744' } },
                  }}
                />
              </Box>

              {/* ------- controls ------- */}
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <IconButton onClick={handleSettingsOpen} sx={{ color: '#ffffff' }}>
                  <SettingsIcon sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }} />
                </IconButton>
                <Tooltip title="Export to CSV">
                  <IconButton onClick={exportToCSV} sx={{ color: '#bbbbbb', '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' } }}>
                    <DownloadIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* ------- Metrics Panel ------- */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 1 : 1.5,
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '6px',
              px: isMobile ? 1 : 1.5,
              py: 0.5,
              mt: isMobile ? 0.5 : 1,
              overflowX: isMobile ? 'auto' : 'visible',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}>
              {metricsData.map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: isMobile ? '0.55rem' : '0.65rem', color: '#ffffff', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem', color: '#ccc', lineHeight: 1.3 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Date Range Popover */}
          <Popover
            open={Boolean(dateAnchorEl)}
            anchorEl={dateAnchorEl}
            onClose={() => setDateAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, width: 280, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <Stack spacing={1.5}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="date-preset-label" shrink sx={{ fontSize: '0.875rem' }}>Quick Select</InputLabel>
                  <Select
                    labelId="date-preset-label"
                    value={DATE_PRESETS.some(p => p.value === dateRange) ? dateRange : ''}
                    label="Quick Select"
                    notched
                    onChange={handlePresetChange}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                    renderValue={(val) => {
                      const preset = DATE_PRESETS.find(p => p.value === val);
                      return preset ? preset.label : 'Custom';
                    }}
                  >
                    {DATE_PRESETS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField type="date" label="From" size="small" fullWidth
                  value={customDateFrom} onChange={handleCustomDateFrom}
                  InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem', color: '#aaa' } }}
                  sx={{ '& input': { fontSize: '0.875rem', color: '#ffffff' }, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                />
                <TextField type="date" label="To" size="small" fullWidth
                  value={customDateTo} onChange={handleCustomDateTo}
                  InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem', color: '#aaa' } }}
                  sx={{ '& input': { fontSize: '0.875rem', color: '#ffffff' }, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setDateRange('all'); setCustomDateFrom(''); setCustomDateTo(''); setDateAnchorEl(null); }}
                  sx={{ color: '#aaa', fontSize: '0.8rem', '&:hover': { backgroundColor: '#333' } }}>
                  Clear
                </Button>
                <Button size="small" onClick={() => setDateAnchorEl(null)}
                  sx={{ color: '#ff1744', fontSize: '0.8rem', '&:hover': { backgroundColor: '#333' } }}>
                  Done
                </Button>
              </Stack>
            </Box>
          </Popover>

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box sx={{ p: '12px 16px', width: 320, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hidePastBets}
                    onChange={() => setHidePastBets(prev => !prev)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Past Bets</Typography>}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={handleSettingsClose} sx={{ color: '#ffffff', fontSize: '0.875rem', minWidth: '80px', '&:hover': { backgroundColor: '#333333' } }}>
                  Cancel
                </Button>
                <Button onClick={handleSettingsSave} sx={{ color: '#ff1744', fontSize: '0.875rem', minWidth: '80px', '&:hover': { backgroundColor: '#333333' } }}>
                  Save
                </Button>
              </Stack>
            </Box>
          </Popover>

          {/* DataGrid */}
          <Box className="table-section" sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
              <DataGridPro
                rows={filteredBets}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[50, 100, 250]}
                pagination
                disableColumnMenu
                disableSelectionOnClick
                hideFooterSelectedRowCount
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                components={{ Toolbar: isMobile ? undefined : GridToolbar, LoadingOverlay: LinearProgress }}
                getRowClassName={(params) => params.row.isPastGame ? 'past-bet-row' : ''}
                rowHeight={isMobile ? 52 : undefined}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#1e1e1e',
                    borderBottom: '2px solid #444',
                    position: 'sticky',
                    top: '56px',
                    zIndex: 2,
                  },
                  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                    fontWeight: 500,
                    color: '#ffffff !important',
                  },
                  '& .MuiDataGrid-cell': {
                    paddingY: '0px',
                    color: '#ffffff !important',
                    userSelect: 'none',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiDataGrid-cell[data-field="marketSourceName"]': { padding: 0, borderRight: 'none' },
                  '& .MuiDataGrid-cell[data-field="untrack"]': { borderRight: 'none' },
                  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: '#1a1a1a',
                    '&:hover': { backgroundColor: '#1a1a1a' },
                  },
                  '& .past-bet-row': {
                    backgroundColor: '#2a2a2a',
                    '&:hover': { backgroundColor: '#2a2a2a' },
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    visibility: 'visible',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': {
                    color: '#ffffff !important',
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-iconButtonContainer': {
                    color: '#ffffff !important',
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: '#ffffff !important',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  },
                  '& .MuiSelect-icon': { color: '#ffffff !important' },
                  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                  '& .MuiDataGrid-root': {
                    overflowY: 'auto !important',
                    margin: 0,
                    boxSizing: 'border-box',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowY: 'auto !important',
                  },
                  '& .MuiDataGrid-main': {
                    padding: 0,
                    boxSizing: 'border-box',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff !important',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    boxSizing: 'border-box',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}